import React, { useState, useEffect } from 'react';
import { X, Check, Info } from 'lucide-react';
import { useChatbot } from '../../../contexts/ChatbotContext';
import { useMiniApps } from '../../../hooks/useMiniApps';
import { useDesignColors } from '../../../hooks/useDesignColors';
import { MINI_APP_ICONS, MiniAppId } from '../../../utils/miniAppsIcons';
import { MiniAppDetails } from './MiniAppDetails';

interface MiniAppSelectorProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MiniAppSelector({ isOpen, onClose }: MiniAppSelectorProps) {
  const { chatbotPublicId } = useChatbot();
  const { miniApps, toggleMiniApp, loading } = useMiniApps();
  const { colors } = useDesignColors();
  const [selectedApps, setSelectedApps] = useState<Set<MiniAppId>>(new Set());
  const [activeApp, setActiveApp] = useState<MiniAppId | null>(null);
  const [showDetails, setShowDetails] = useState<MiniAppId | null>(null);

  // Initialize selected apps based on current enabled state
  useEffect(() => {
    const enabled = new Set<MiniAppId>();
    miniApps.forEach(app => {
      if (app.enabled) {
        enabled.add(app.id);
      }
    });
    setSelectedApps(enabled);
  }, [miniApps]);

  const handleToggleApp = (appId: MiniAppId) => {
    setSelectedApps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(appId)) {
        newSet.delete(appId);
      } else {
        newSet.add(appId);
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    if (!chatbotPublicId) return;
    
    // For each app, check if its enabled state needs to change
    for (const app of miniApps) {
      const shouldBeEnabled = selectedApps.has(app.id);
      if (shouldBeEnabled !== app.enabled) {
        await toggleMiniApp(app.id, chatbotPublicId);
      }
    }
    
    onClose();
  };

  const handleShowDetails = (appId: MiniAppId, e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDetails(appId);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-gray-900/95 rounded-2xl border border-white/10 w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h3 className="text-2xl font-semibold bg-clip-text text-transparent bg-gradient-to-r" style={{
            backgroundImage: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`
          }}>Add Mini Apps</h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white transition-colors rounded-full hover:bg-white/5"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {miniApps.map(app => {
              const Icon = MINI_APP_ICONS[app.id];
              const isSelected = selectedApps.has(app.id);
              const isActive = activeApp === app.id;
              
              return (
                <div 
                  key={app.id}
                  className={`relative rounded-xl border backdrop-blur-sm transition-all duration-300 cursor-pointer group ${
                    isSelected 
                      ? 'border-transparent shadow-lg' 
                      : 'border-white/5 bg-white/5 hover:border-white/10 hover:bg-white/10'
                  }`}
                  style={isSelected ? {
                    background: `linear-gradient(135deg, ${colors.gradientStart}20, ${colors.gradientEnd}20)`,
                    boxShadow: `0 8px 24px -8px ${colors.gradientStart}40`
                  } : undefined}
                  onClick={() => handleToggleApp(app.id)}
                  onMouseEnter={() => setActiveApp(app.id)}
                  onMouseLeave={() => setActiveApp(null)}
                >
                  <div className="absolute top-4 right-4 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => handleShowDetails(app.id, e)}
                      className="p-2 rounded-full bg-white/10 text-gray-400 hover:bg-white/20 hover:text-white transition-all"
                    >
                      <Info className="w-4 h-4" />
                    </button>
                    
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                      isSelected ? 'shadow-lg' : 'bg-white/10'
                    }`}
                    style={isSelected ? {
                      background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
                      boxShadow: `0 4px 12px -2px ${colors.gradientStart}40`
                    } : undefined}>
                      {isSelected && <Check className="w-5 h-5 text-white" />}
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <div className="mb-6">
                      <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 transition-all ${
                        isSelected ? 'shadow-lg' : 'bg-gradient-to-br from-white/5 to-white/10'
                      }`}
                      style={isSelected ? {
                        background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
                        boxShadow: `0 8px 24px -8px ${colors.gradientStart}40`
                      } : undefined}>
                        <Icon className={`w-8 h-8 transition-all ${
                          isSelected ? 'text-white' : 'text-gray-400 group-hover:text-white'
                        }`} />
                      </div>
                      
                      <h4 className="text-xl font-semibold text-white mb-2">{app.title}</h4>
                      <p className="text-gray-400 text-sm leading-relaxed">
                        {getAppDescription(app.id)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        <div className="p-6 border-t border-white/10 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-xl bg-white/5 text-gray-300 hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-6 py-2.5 rounded-xl text-white transition-all flex items-center gap-2 shadow-lg disabled:opacity-50"
            style={{
              background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
              boxShadow: `0 8px 24px -8px ${colors.gradientStart}40`
            }}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* Mini App Details Modal */}
      {showDetails && (
        <MiniAppDetails
          appId={showDetails}
          isOpen={true}
          onClose={() => setShowDetails(null)}
          onToggle={() => {
            handleToggleApp(showDetails);
            setShowDetails(null);
          }}
          isEnabled={selectedApps.has(showDetails)}
        />
      )}
    </div>
  );
}

// Helper function to get app descriptions
function getAppDescription(appId: MiniAppId): string {
  switch (appId) {
    case 'products':
      return 'Showcase your products with an interactive catalog, including images, descriptions, and prices.';
    case 'calendar':
      return 'Allow users to schedule appointments directly from the chat with Calendly integration.';
    case 'services':
      return 'Present your services with a visual and interactive carousel, including descriptions and pricing.';
    case 'videos':
      return 'Share your YouTube, Vimeo, or custom videos directly in the chat for an immersive experience.';
    case 'gallery':
      return 'Create interactive photo galleries to showcase your achievements, products, or events.';
    case 'realestate':
      return 'Present your real estate properties with a dedicated interface including photos and details.';
    case 'social':
      return 'Connect with your audience through social media links displayed as interactive buttons.';
    case 'order-tracking':
      return 'Allow customers to track their orders by providing an order number.';
    default:
      return 'Add this mini app to enhance your chatbot experience.';
  }
}